import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from 'react-bootstrap';
import { OverviewBody } from '../../Pages/Orders/Components/AIOverviewPanel';

/**
 * Renders one chat block — text, tool-progress chip, file card, image,
 * or structured question. The widget decides layout / ordering; this
 * component just renders the block it's given.
 */

// Strips the structured ```json bookings-proposal``` fenced block from
// assistant text so the operator sees the surrounding explanation, not
// the raw 5–20 KB JSON payload. The structured proposal renders in the
// IngestWorkflow's <BookingProposalGrid> instead. Marker text mirrors
// the workflow chip used elsewhere in the dashboard for consistency.
const BOOKINGS_PROPOSAL_FENCE_RE = /```json\s+bookings-proposal[\s\S]*?```/g;
const BOOKINGS_PROPOSAL_PLACEHOLDER = '\n\n_✨ Drafted booking proposal — see the grid on the right →_\n\n';

function TextBlock({ role, content, streaming }) {
  const isUser = role === 'user';
  const className = [
    'chat-widget-message',
    isUser ? 'chat-widget-message-user' : 'chat-widget-message-assistant',
    streaming ? 'chat-widget-message-streaming' : null,
  ].filter(Boolean).join(' ');

  let displayContent = content || '';
  if (!isUser) {
    displayContent = displayContent.replace(
      BOOKINGS_PROPOSAL_FENCE_RE,
      BOOKINGS_PROPOSAL_PLACEHOLDER,
    );
  }

  return (
    <div className={className}>
      {isUser ? <span>{content}</span> : (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
      )}
    </div>
  );
}

const TOOL_STATUS_ICONS = {
  pending: 'fa-spinner fa-spin',
  error: 'fa-triangle-exclamation',
  done: 'fa-check',
};

function ToolBlock({ name, summary, status }) {
  const icon = TOOL_STATUS_ICONS[status] ?? 'fa-check';
  return (
    <div className={`chat-widget-tool chat-widget-tool-${status}`}>
      <i className={`fa ${icon}`} aria-hidden="true" />
      <span className="chat-widget-tool-text">{summary || name}</span>
    </div>
  );
}

/**
 * MIME type → the short label an operator recognises.
 *
 * Printing `contentType` verbatim stopped being viable when the chat agent
 * started emitting its own exports on 2026-11-01: the payout workbook's type
 * is `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`, 65
 * characters in a chip sized for `application/pdf` and wider than the card it
 * sits in.
 *
 * A MAP AND NOT A DERIVATION, because every cheap derivation is wrong here.
 * Taking the last dotted segment of that subtype gives "SHEET" — a test
 * caught exactly that. Taking the filename's extension gives the right answer
 * but duplicates text the operator can already read two spans to the left.
 * So: the types this app actually hands out, and nothing for a type that is
 * not one of them, because the filename beside the chip already says what the
 * file is. Add an entry when a tool starts producing a new type — the
 * producers are named in the API's `tools/_exports.py`.
 */
const FILE_TYPE_LABELS = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'text/csv': 'CSV',
};

function fileTypeLabel(contentType) {
  if (!contentType || typeof contentType !== 'string') return null;
  // Strip any `; charset=…` parameter before matching.
  return FILE_TYPE_LABELS[contentType.split(';')[0].trim().toLowerCase()] || null;
}

function FileBlock({
  filename, url, contentType, expiresAt,
}) {
  const type = fileTypeLabel(contentType);
  // These links are presigned and they die: 2h for the payout workbook, 12h
  // for a voucher (`tools/_exports.py` says why the two differ). Without the
  // expiry on the card, an operator who comes back from lunch clicks it and
  // gets an S3 XML error page that explains nothing. Shown as a wall-clock
  // time in the operator's own timezone — the event carries an absolute
  // instant precisely so this does not have to guess.
  const expiry = expiresAt ? new Date(expiresAt) : null;
  const expiryLabel = expiry && !Number.isNaN(expiry.valueOf())
    ? expiry.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <a className="chat-widget-file" href={url} target="_blank" rel="noopener noreferrer" download={filename}>
      <i className="fa fa-file-arrow-down" aria-hidden="true" />
      <span className="chat-widget-file-name">{filename}</span>
      {type && <span className="chat-widget-file-type">{type}</span>}
      {expiryLabel && (
        <span className="chat-widget-file-type" title={`This download link expires at ${expiresAt}`}>
          {`expires ${expiryLabel}`}
        </span>
      )}
    </a>
  );
}

function ImageBlock({ url, altText, caption }) {
  return (
    <figure className="chat-widget-image">
      <img src={url} alt={altText || ''} />
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

function QuestionBlock({
  block, onAnswer,
}) {
  return (
    <div className="chat-widget-question">
      <div className="chat-widget-question-prompt">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{block.prompt}</ReactMarkdown>
      </div>
      {block.choices && (
        <div className="chat-widget-question-choices">
          {block.choices.map((choice) => (
            <Button
              key={choice.value}
              variant="outline-primary"
              size="sm"
              disabled={block.answered}
              onClick={() => onAnswer(block.id, choice.value)}
            >
              {choice.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

// Compact number: 1234 → 1.2k. Keeps the chip narrow.
function fmt(n) {
  if (n == null) return '0';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

// Friendly USD formatting for tiny per-turn amounts. Most chats are
// fractions of a cent thanks to prompt caching; round dollars only
// happen for huge multi-step turns.
function fmtCost(usd) {
  if (usd == null) return null;
  if (usd === 0) return '0¢';
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  if (usd >= 0.01) return `${(usd * 100).toFixed(1)}¢`;
  if (usd >= 0.001) return `${(usd * 100).toFixed(2)}¢`;
  return '<0.1¢';
}

function UsageBlock({
  input, output, cacheRead, cacheWrite, estimatedCostUsd,
}) {
  // Token cost for the operator's mental model:
  //   ↑ fresh input (charged at full rate)
  //   ⚡ cache read (charged at ~10× lower rate — the win from caching)
  //   ↓ output (charged separately, usually small)
  // Cache write only matters on the first turn of a session; show it
  // when non-zero so the operator can see the cache being primed.
  return (
    <div className="chat-widget-usage" aria-label="Token usage for this turn">
      <span title="Fresh input tokens (system prompt + tool defs + new messages)">
        ↑
        {' '}
        {fmt(input)}
      </span>
      {cacheRead > 0 && (
        <span title="Tokens served from prompt cache — ~10× cheaper than fresh">
          ⚡
          {' '}
          {fmt(cacheRead)}
        </span>
      )}
      {cacheWrite > 0 && (
        <span title="Tokens written to cache (only happens on first turn of a session)">
          ✎
          {' '}
          {fmt(cacheWrite)}
        </span>
      )}
      <span title="Output tokens generated by the model">
        ↓
        {' '}
        {fmt(output)}
      </span>
      {estimatedCostUsd != null && (
        <span
          className="chat-widget-usage-cost"
          title={`Estimated cost for this turn (~$${estimatedCostUsd.toFixed(6)} USD)`}
        >
          ≈
          {' '}
          {fmtCost(estimatedCostUsd)}
        </span>
      )}
    </div>
  );
}

function ApprovalBlock({ block, onDecide }) {
  // Render any tool argument the agent thought worth surfacing. Nested
  // dicts get flattened lightly; lists and primitives stringify.
  const renderValue = (value) => {
    if (value == null) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const decided = block.decision != null;
  const approved = block.decision === 'approve';
  const rejected = block.decision === 'reject';

  return (
    <div className={`chat-widget-approval${decided ? ` chat-widget-approval-${block.decision}` : ''}`}>
      <div className="chat-widget-approval-header">
        <i className="fa fa-shield-halved" aria-hidden="true" />
        <span className="chat-widget-approval-action">{block.action}</span>
      </div>
      {Object.keys(block.details || {}).length > 0 && (
        <dl className="chat-widget-approval-details">
          {Object.entries(block.details).map(([key, value]) => (
            <React.Fragment key={key}>
              <dt>{key}</dt>
              <dd>{renderValue(value)}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      <div className="chat-widget-approval-actions">
        <Button
          size="sm"
          variant="success"
          disabled={decided}
          onClick={() => onDecide(block.id, 'approve')}
        >
          {approved ? 'Approved' : 'Approve'}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={decided}
          onClick={() => onDecide(block.id, 'reject')}
        >
          {rejected ? 'Rejected' : 'Reject'}
        </Button>
      </div>
    </div>
  );
}

// Mapbox marker colour palette — hex without the `#` so it slots into
// the static-image URL syntax (`pin-s+22ad44(...)`).
const MAP_PIN_COLORS = {
  green: '22ad44',
  red: 'ee4444',
  blue: '3b82f6',
  yellow: 'eab308',
};

function MapBlock({ points }) {
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  const valid = (points || []).filter((p) => (
    p && Number.isFinite(p.lat) && Number.isFinite(p.lng)
  ));
  if (!token || valid.length === 0) return null;

  // Build the markers segment: `pin-s+<hex>(<lng>,<lat>),pin-s+<hex>(...)`.
  const overlay = valid.map((p) => {
    const color = MAP_PIN_COLORS[p.color] || MAP_PIN_COLORS.blue;
    return `pin-s+${color}(${p.lng.toFixed(5)},${p.lat.toFixed(5)})`;
  }).join(',');

  // `auto/` lets Mapbox pick zoom + centre to fit all markers when there
  // are 2+. For a single point we lock zoom to 13 (city block scale).
  const view = valid.length === 1
    ? `${valid[0].lng.toFixed(5)},${valid[0].lat.toFixed(5)},13`
    : 'auto';

  const url = (
    `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${overlay}/`
    + `${view}/420x220@2x?access_token=${encodeURIComponent(token)}`
  );

  // Click → open an interactive Mapbox view of the same area in a new
  // tab, in case the operator wants pan/zoom for closer inspection.
  const interactiveUrl = `https://www.mapbox.com/maps/streets?center=${valid[0].lng},${valid[0].lat}&zoom=13`;

  return (
    <div className="chat-widget-map">
      <a href={interactiveUrl} target="_blank" rel="noopener noreferrer" title="Open in Mapbox">
        <img src={url} alt="Map preview" className="chat-widget-map-image" />
      </a>
      <ul className="chat-widget-map-legend">
        {valid.map((p) => (
          <li key={`${p.lat},${p.lng},${p.label || ''},${p.color || ''}`}>
            <span
              className="chat-widget-map-pin"
              style={{ backgroundColor: `#${MAP_PIN_COLORS[p.color] || MAP_PIN_COLORS.blue}` }}
            />
            <span className="chat-widget-map-label">{p.label || `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A booking briefing in the chat, rendered with the SAME component as the
 * panel at the top of the booking modal (`OverviewBody`).
 *
 * Sharing the component is the point rather than a convenience: the gaps,
 * the sources and the "Looked for, not found" affordance are the feature's
 * honesty mechanism, and there is one piece of code deciding how they look
 * whichever surface asked for the briefing. A second rendering of them here
 * would be a second chance to quietly drop one.
 */
function OverviewBlock({ bookingId, overview }) {
  if (!overview?.summary) return null;
  return (
    <div className="chat-widget-overview" data-testid="chat-overview">
      <div className="ai-overview-head">
        <span className="ai-overview-title">
          <i className="fa fa-wand-magic-sparkles me-2" aria-hidden="true" />
          AI overview
        </span>
        {bookingId && <code className="chat-widget-overview-ref">{bookingId}</code>}
      </div>
      <OverviewBody overview={overview} />
    </div>
  );
}

function ChatMessage({ block, onAnswer, onDecideApproval }) {
  switch (block.kind) {
    case 'text':
      return <TextBlock role={block.role} content={block.content} streaming={block.streaming} />;
    case 'tool':
      return <ToolBlock name={block.name} summary={block.summary} status={block.status} />;
    case 'file':
      return (
        <FileBlock
          filename={block.filename}
          url={block.url}
          contentType={block.contentType}
          expiresAt={block.expiresAt}
        />
      );
    case 'image':
      return <ImageBlock url={block.url} altText={block.altText} caption={block.caption} />;
    case 'question':
      return <QuestionBlock block={block} onAnswer={onAnswer} />;
    case 'approval':
      return <ApprovalBlock block={block} onDecide={onDecideApproval} />;
    case 'usage':
      return (
        <UsageBlock
          input={block.input}
          output={block.output}
          cacheRead={block.cacheRead}
          cacheWrite={block.cacheWrite}
          estimatedCostUsd={block.estimatedCostUsd}
        />
      );
    case 'map':
      return <MapBlock points={block.points} />;
    case 'overview':
      return <OverviewBlock bookingId={block.bookingId} overview={block.overview} />;
    default:
      return null;
  }
}

export default ChatMessage;
